/**
 * ResizePrecisionTip — makes the exact-resize path discoverable.
 *
 * The grid has shipped keyboard move/resize since 0.8 (Enter/Space grabs,
 * arrows move, Shift+Arrows resize) with live-region announcements. It is
 * wired to an `aria-describedby` help node on every grid, so screen-reader
 * users are told about it — and nobody else is. Sighted authors fighting a
 * small handle have had a precise, no-aim path available the whole time and no
 * way to find out.
 *
 * Input-aware: a finger has no Shift key, so on a coarse pointer the tip
 * points at the bar's Size fields instead of advertising a chord the reader
 * cannot press.
 *
 * Shown when a block is selected, i.e. exactly when the next thing the author
 * is likely to do is resize it. Dismissable and remembered, like
 * SelectFirstTip — a permanent bar would be noise once learned. Sits in the
 * bottom-centre stack above the selection bar, at a `bottom` derived from the
 * bar's measured height (it wraps to two rows on a phone).
 */
import { useState } from "react";
import { css, html } from "react-strict-dom";
import { useGridStore } from "@fredericrous/grid";

import { stackBottom, useBarHeight } from "./bottomStack.js";
import { coachPill } from "./coachPill.js";
import { usePointerCoarse } from "./usePointerCoarse.js";

const SEEN_KEY = "wb-resize-precision-tip-v1";

const s = css.create({
  at: (bottom: number) => ({ bottom }),
});

function alreadySeen(): boolean {
  try {
    return (
      typeof localStorage !== "undefined" &&
      localStorage.getItem(SEEN_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export function ResizePrecisionTip(): React.ReactElement | null {
  const [seen, setSeen] = useState(alreadySeen);
  // Only while something is selected — the tip is about what to do NEXT.
  const hasSelection = useGridStore(
    (s) => (s.selection?.itemIds.length ?? 0) > 0,
  );
  const coarse = usePointerCoarse();
  const barHeight = useBarHeight();

  if (seen || !hasSelection) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(SEEN_KEY, "1");
    } catch {
      // private-mode / storage-disabled: still dismiss for this session.
    }
    setSeen(true);
  };

  return (
    <html.div
      role="status"
      aria-live="polite"
      // Top of the bottom-centre stack: bar, SelectFirstTip, this.
      style={[coachPill.pill, s.at(stackBottom(barHeight, 1))]}
    >
      {coarse ? (
        <html.span>
          📐 Exact size: use <html.strong>W</html.strong> and{" "}
          <html.strong>H</html.strong> in the bar below
        </html.span>
      ) : (
        <html.span>
          ⌨️ <html.strong>Shift + arrows</html.strong> resizes exactly ·{" "}
          <html.strong>arrows</html.strong> move
        </html.span>
      )}
      <html.button type="button" onClick={dismiss} style={coachPill.dismiss}>
        Got it
      </html.button>
    </html.div>
  );
}
